/**
 * Edit which weekdays a student may leave school without pickup.
 * Selection is the child.leaves_alone cache (ISO 1–5). Profile PATCH runs once
 * on leave (back / in-app navigation), not on each toggle.
 */
import type { Child } from "@spork/s4p-server-sdk";
import { useNavigation } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { toastError } from "@/components/AppToast";
import type { SelectOption } from "@/components/ui/enum-select";
import { SelectOptionList } from "@/components/ui/select-option-list";
import { getLeavesAloneWeekdays } from "@/features/home/leavesAlone";
import {
    LEAVES_ALONE_FIELD_CODE,
    getProfileFieldDescription,
} from "@/features/profile";
import { log } from "@/logger";
import { usePatchProfileMutation } from "@/query/apiSlice";

import {
    SCHOOL_WEEKDAYS,
    toLeavesAloneOptionIds,
    toggleSchoolWeekday,
} from "./leaveDaysProfilePatch";

type LeaveDaysScreenProps = {
    child: Child;
};

type NavigationAction = {
    type: string;
    payload?: object;
    source?: string;
    target?: string;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }
    return fallback;
}

function weekdaysKey(days: number[]): string {
    return days.join(",");
}

/**
 * Saves dirty leave-day edits on back / leaving the screen, then continues navigation.
 * Stays put when the profile PATCH fails. Unchanged selection is not intercepted.
 */
function useSaveLeaveDaysOnLeave(
    isDirty: boolean,
    saveIfDirty: () => Promise<boolean>,
) {
    const navigation = useNavigation();
    const allowLeaveRef = useRef(false);
    const savingRef = useRef(false);

    useEffect(() => {
        const unsubscribe = navigation.addListener("beforeRemove", (event) => {
            if (allowLeaveRef.current || !isDirty) {
                return;
            }

            event.preventDefault();
            if (savingRef.current) {
                return;
            }

            savingRef.current = true;
            const action = event.data.action as NavigationAction;
            void saveIfDirty().then((didSucceed) => {
                if (!didSucceed) {
                    savingRef.current = false;
                    return;
                }
                allowLeaveRef.current = true;
                navigation.dispatch(action);
            });
        });

        return unsubscribe;
    }, [navigation, isDirty, saveIfDirty]);
}

export function LeaveDaysScreen({ child }: LeaveDaysScreenProps) {
    const { t } = useTranslation();
    const [patchProfile] = usePatchProfileMutation();
    const serverDays = getLeavesAloneWeekdays(child.leaves_alone);
    const [selectedDays, setSelectedDays] = useState(serverDays);
    const [isSaving, setIsSaving] = useState(false);

    const isDirty = weekdaysKey(selectedDays) !== weekdaysKey(serverDays);

    const saveIfDirty = useCallback(async () => {
        if (weekdaysKey(selectedDays) === weekdaysKey(serverDays)) {
            return true;
        }

        setIsSaving(true);
        try {
            await patchProfile({
                id: child.pipedrive_id,
                data: {
                    custom_fields: {
                        [LEAVES_ALONE_FIELD_CODE]:
                            toLeavesAloneOptionIds(selectedDays),
                    },
                },
            }).unwrap();
            return true;
        } catch (error) {
            log.error("patchProfile leave days failed", error);
            toastError(
                t("profile.saveFailed"),
                getErrorMessage(error, t("profile.saveFailed")),
            );
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [selectedDays, serverDays, child.pipedrive_id, patchProfile, t]);

    useSaveLeaveDaysOnLeave(isDirty, saveIfDirty);

    const options: SelectOption[] = SCHOOL_WEEKDAYS.map((day) => ({
        id: day,
        label: t(`ui.weekday.${day}`),
    }));

    function handleOptionPress(option: SelectOption) {
        if (isSaving) {
            return;
        }
        setSelectedDays(toggleSchoolWeekday(selectedDays, option.id));
    }

    return (
        <SelectOptionList
            options={options}
            isSelected={(optionId) => selectedDays.includes(optionId)}
            multiple
            description={getProfileFieldDescription(LEAVES_ALONE_FIELD_CODE)}
            onOptionPress={handleOptionPress}
        />
    );
}
